import React, { useState, useEffect } from 'react';
import { Calendar, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, MapPin, User, Users, Link as LinkIcon } from 'lucide-react';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import CustomScrollbar from '../../components/UI/CustomScrollbar';
import { useGuestDashboard } from '../../hooks/useDashboardData';
import { useAppSelector } from '../../store/hooks';
import { guestAPI } from '../../services/guestAPI';
import { webSocketService } from '../../services/websocketService';
import { authAPI } from '../../services/api';
import { setUser } from '../../store/slices/authSlice';
import { store } from '../../store/store';
import { logError } from '../../utils/errorLogger';
import { getImageShapeStyles } from '../../utils/imageShapeStyles';

const GuestDashboard: React.FC = () => {
  const { data: statsData, loading: statsLoading, error: statsError, refetch } = useGuestDashboard();
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  
  const [respondingTo, setRespondingTo] = useState<number | null>(null);
  const [responseMessage, setResponseMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<any>(null);
  const [selectedAction, setSelectedAction] = useState<'accepted' | 'rejected' | null>(null);

  const [showCompanionModal, setShowCompanionModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState<any>(null);
  const [companionResponses, setCompanionResponses] = useState<Array<{
    guest_event_id: string;
    name: string;
    status: 'accepted' | 'rejected' | 'pending';
  }>>([]);

  // Refresh user profile on dashboard load to get latest roles
  // This ensures we have the most up-to-date roles (e.g., if admin added host role after login)
  useEffect(() => {
    const refreshUserProfile = async () => {
      try {
        const profileResponse = await authAPI.getProfile();
        if (profileResponse.data.success && profileResponse.data.data) {
          const updatedUser = {
            ...profileResponse.data.data,
            roles: profileResponse.data.data.roles || [profileResponse.data.data.role],
            active_role: profileResponse.data.data.active_role || profileResponse.data.data.role,
          };
          store.dispatch(setUser(updatedUser));
        }
      } catch (err) {
        logError('Failed to refresh user profile', err);
      }
    };
    
    // Always refresh profile on dashboard load to get latest roles
    // This is important because roles can change (e.g., admin adds host role) after login
    refreshUserProfile();
  }, []); // Only run once on mount

  React.useEffect(() => {
    // Stats data loaded
  }, [statsData, statsLoading, statsError, user?.roles]);

  // Real-time WebSocket updates
  useEffect(() => {
    const handleGuestResponse = (data: any) => {
      // Refresh dashboard data when any guest responds
      refetch().catch((error) => {
        logError('Guest dashboard refetch failed', error);
      });
    };

    const handleStatisticsUpdate = (data: any) => {
      refetch().catch((error) => {
        logError('Guest dashboard statistics refetch failed', error);
      });
    };

    // Subscribe to real-time updates
    const unsubscribeGuestResponse = webSocketService.on('guest_response', handleGuestResponse);
    const unsubscribeStatistics = webSocketService.on('event_statistics_update', handleStatisticsUpdate);

    return () => {
      unsubscribeGuestResponse();
      unsubscribeStatistics();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to prevent re-subscription

  // Handle opening confirmation modal
  const handleResponseClick = (invitation: any, action: 'accepted' | 'rejected') => {
    setSelectedInvitation(invitation);
    setSelectedAction(action);
    
    // Initialize companion responses with current status (for potential future usage in other flows)
    // But we don't show companion selection in the initial confirm modal anymore
    setCompanionResponses([]);
    
    setShowConfirmModal(true);
  };

  // Handle opening companion management modal
  const handleManageCompanions = (invitation: any) => {
    setSelectedInvitation(invitation);
    
    if (invitation.companions && invitation.companions.length > 0) {
      setCompanionResponses(invitation.companions.map((c: any) => ({
        guest_event_id: c.guest_event_id,
        name: c.name,
        status: c.status
      })));
    } else {
      setCompanionResponses([]);
    }
    
    setShowCompanionModal(true);
  };

  // Handle companion status change
  const handleCompanionStatusChange = (index: number, status: 'accepted' | 'rejected') => {
    const newResponses = [...companionResponses];
    if (newResponses[index]) {
      newResponses[index].status = status;
      setCompanionResponses(newResponses);
    }
  };

  // Save companion changes
  const handleSaveCompanions = async () => {
    if (!selectedInvitation) return;

    try {
      setRespondingTo(selectedInvitation.guest_event_id);
      setResponseMessage(null);
      setShowCompanionModal(false);
      
      const token = selectedInvitation.invitation_token || selectedInvitation.id;
      
      // We assume the main guest is already 'accepted' if they are editing companions
      // Send 'accepted' as the main status along with updated companion responses
      await guestAPI.respondToInvitation(token, 'accepted', companionResponses);
      
      setResponseMessage({
        type: 'success',
        text: 'Companion list updated successfully'
      });
      
      await refetch();
      
    } catch (error: any) {
      logError('Failed to update companions', error);
      setResponseMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update companions'
      });
    } finally {
      setRespondingTo(null);
      setSelectedInvitation(null);
      setCompanionResponses([]);

      setTimeout(() => {
        setResponseMessage(null);
      }, 3000);
    }
  };

  // Handle opening event details modal
  const handleViewDetails = (invitation: any) => {
    setSelectedEventDetails(invitation);
    setShowEventDetailsModal(true);
  };

  // Format time to 12-hour format
  const formatTime = (timeString: string) => {
    try {
      if (typeof timeString === 'string' && timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const timeDate = new Date();
        timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        return timeDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      
      const timeDate = new Date(timeString);
      if (!isNaN(timeDate.getTime())) {
        return timeDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      
      return timeString || 'Time not available';
    } catch (error) {
      return timeString || 'Time not available';
    }
  };

  // Check if event is past
  const isEventPast = (invitation: any) => {
    try {
      if (invitation.is_past) return true;
      
      const [timeHours, timeMinutes] = invitation.time.split(':');
      const eventDate = new Date(invitation.date);
      eventDate.setHours(parseInt(timeHours), parseInt(timeMinutes), 0, 0);
      
      const now = new Date();
      return eventDate.getTime() <= now.getTime();
    } catch (error) {
      return false;
    }
  };


  // Handle confirmed response
  const handleConfirmedResponse = async () => {
    if (!selectedInvitation || !selectedAction) return;

    try {
      setRespondingTo(selectedInvitation.guest_event_id);
      setResponseMessage(null);
      setShowConfirmModal(false);
      
      // Use invitation_token if available, otherwise fall back to id
      const token = selectedInvitation.invitation_token || selectedInvitation.id;
      
      // Just send the main response. Companion updates are now handled in a separate modal.
      const result = await guestAPI.respondToInvitation(token, selectedAction, []);
      
      setResponseMessage({
        type: 'success',
        text: result.message
      });
      
      // Refresh the dashboard data
      await refetch();
      
    } catch (error: any) {
      logError('Guest dashboard response failed', error);
      setResponseMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to respond to invitation'
      });
    } finally {
      setRespondingTo(null);
      setSelectedInvitation(null);
      setSelectedAction(null);
      setCompanionResponses([]);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setResponseMessage(null);
      }, 3000);
    }
  };

  // Handle guest response to invitation (legacy function - keeping for compatibility)
  // Note: This function is kept for compatibility but is currently unused
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInvitationResponse = async (eventId: string, guestEventId: number, status: 'accepted' | 'rejected') => {
    try {
      setRespondingTo(guestEventId);
      setResponseMessage(null);
      
      // This function signature is deprecated - use respondToInvitation with token instead
      // For now, we'll need the invitation token to call the new API
      // This function should be updated to receive token as parameter
      logError('handleInvitationResponse called with deprecated signature', { eventId, guestEventId, status });
      setResponseMessage({
        type: 'error',
        text: 'This function requires invitation token. Please use the new API with token.'
      });
      return;
      
    } catch (error: any) {
      logError('Guest dashboard response failed', error);
      setResponseMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to respond to invitation'
      });
    } finally {
      setRespondingTo(null);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setResponseMessage(null);
      }, 3000);
    }
  };

  // Check if user's active role is guest - if not, show error or redirect
  const activeRole = user?.active_role || user?.role;
  if (activeRole !== 'guest') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className={`rounded-lg p-6 ${
          theme === 'dark' 
            ? 'bg-red-900/30 border border-red-700/50' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>
                Access Denied
              </h3>
              <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>
                You are currently viewing as {activeRole}. Please switch to guest role to view the guest dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (statsLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
            <p className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (statsError) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-red-900/20 border-red-700/50' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="p-6">
          <div className="flex items-center">
            <div className="text-red-600">
              <Calendar className="h-6 w-6" />
            </div>
            <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-red-300' : 'text-red-800'
                }`}>Error Loading Dashboard</h3>
                <p className={`mt-1 text-sm ${
                  theme === 'dark' ? 'text-red-200' : 'text-red-700'
                }`}>{statsError}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const invitations = statsData?.upcoming_events || [];
  const pendingCount = statsData?.invitations.pending || 0;
  const acceptedCount = statsData?.invitations.accepted || 0;
  const rejectedCount = statsData?.invitations.rejected || 0;


  const getStatusBadge = (status: string, isPast: boolean = false) => {
    if (isPast) {
      return theme === 'dark' 
        ? 'bg-gray-800/50 text-gray-400 border border-gray-600/50' 
        : 'bg-gray-100 text-gray-500';
    }
    
    switch (status) {
      case 'accepted':
        return theme === 'dark' 
          ? 'bg-[#33FF00]/20 text-[#33FF00] border border-[#33FF00]/30' 
          : 'bg-mirsal-green text-mirsal-black';
      case 'rejected':
        return theme === 'dark' 
          ? 'bg-red-900/30 text-red-300 border border-red-700/50' 
          : 'bg-red-100 text-red-800';
      case 'pending':
        return theme === 'dark' 
          ? 'bg-orange-900/30 text-orange-300 border border-orange-700/50' 
          : 'bg-orange-100 text-orange-800';
      default:
        return theme === 'dark' 
          ? 'bg-gray-800/50 text-gray-300 border border-gray-600/50' 
          : 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <div className="max-w-6xl mx-auto">
      {/* SVG def for heart-shaped event images (used in modals) */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <clipPath id="heartClipPath" clipPathUnits="objectBoundingBox">
            <path d="M0.5,1 C0.5,1 0,0.65 0,0.35 A0.25,0.25 0 0,1 0.5,0.35 A0.25,0.25 0 0,1 1,0.35 C1,0.65 0.5,1 0.5,1 Z" />
          </clipPath>
        </defs>
      </svg>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>My Invitations</h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              View and respond to your event invitations
            </p>
          </div>
        </div>
      </div>

      {/* Response Message */}
      {responseMessage && (
        <div className={`rounded-lg p-4 ${
          responseMessage.type === 'success' 
            ? (theme === 'dark' ? 'bg-green-900/20 border-green-700/50 text-green-300' : 'bg-green-50 border-green-200 text-green-800')
            : (theme === 'dark' ? 'bg-red-900/20 border-red-700/50 text-red-300' : 'bg-red-50 border-red-200 text-red-800')
        } border`}>
          <div className="flex items-center">
            {responseMessage.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <XCircle className="h-5 w-5 mr-2" />
            )}
            <span className="font-medium">{responseMessage.text}</span>
          </div>
        </div>
      )}

      {/* Stats Cards — aligned with Admin/Host dashboard */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
        <EnhancedLightCard
          title="Pending Responses"
          value={pendingCount}
          icon={<AlertCircle />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Accepted Events"
          value={acceptedCount}
          icon={<CheckCircle />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Rejected Events"
          value={rejectedCount}
          icon={<XCircle />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Total Invitations"
          value={invitations.length}
          icon={<Calendar />}
          compactHorizontal
        />
      </div>

      {/* Help Section */}
      {pendingCount > 0 && (
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-blue-900/20 border-blue-700/50' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="p-6">
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5" />
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                }`}>
                  You have {pendingCount} pending invitation{pendingCount !== 1 ? 's' : ''}
                </h3>
                <p className={`mt-1 text-sm ${
                  theme === 'dark' ? 'text-blue-200' : 'text-blue-700'
                }`}>
                  Please respond to your invitations so hosts can plan accordingly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitations List */}
      <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
        theme === 'dark' 
          ? 'bg-black/80 border-gray-800/50' 
          : 'bg-white/90 border-gray-200/50'
      }`}>
        <div className={`px-6 py-4 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-white' : 'text-black'
          }`}>Event Invitations</h3>
        </div>
        <CustomScrollbar maxHeight="max-h-96">
          <div className={`divide-y ${
            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
          {invitations.length > 0 ? (
            invitations.map((invitation) => (
              <div key={invitation.id} className={`group relative p-6 transition-all duration-300 hover:bg-opacity-50 ${
                theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
              }`}>
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Left: Date Ticket Stub */}
                  <div className={`flex-shrink-0 flex flex-col items-center justify-center w-20 h-20 rounded-2xl border-2 ${
                    theme === 'dark' 
                        ? 'bg-gray-800 border-gray-700 text-gray-300' 
                        : 'bg-white border-gray-100 text-gray-900 shadow-sm'
                  }`}>
                    <span className="text-xs font-bold uppercase tracking-wider opacity-60">
                        {new Date(invitation.date).toLocaleString('default', { month: 'short' })}
                    </span>
                    <span className="text-2xl font-bold">
                        {new Date(invitation.date).getDate()}
                    </span>
                  </div>

                  {/* Middle: Event Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                        <h4 className={`text-lg font-bold truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {invitation.title}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invitation.status, isEventPast(invitation))}`}>
                            {isEventPast(invitation) ? 'Ended' : invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                        </span>
                    </div>
                    
                    <div className={`flex flex-wrap items-center gap-y-1 gap-x-4 text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1.5 opacity-70" />
                            {formatTime(invitation.time)}
                        </div>
                        <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1.5 opacity-70" />
                            <span className="truncate max-w-[200px]">{invitation.location}</span>
                        </div>
                        <div className="flex items-center">
                            <User className="h-4 w-4 mr-1.5 opacity-70" />
                            <span>{invitation.host}</span>
                        </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex-shrink-0 flex items-center gap-3 mt-4 md:mt-0">
                    {invitation.status === 'pending' && !isEventPast(invitation) ? (
                        <>
                            <button
                                onClick={() => handleResponseClick(invitation, 'rejected')}
                                disabled={respondingTo === invitation.guest_event_id}
                                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                                    theme === 'dark' 
                                        ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                                } ${respondingTo === invitation.guest_event_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Decline
                            </button>
                            <button
                                onClick={() => handleResponseClick(invitation, 'accepted')}
                                disabled={respondingTo === invitation.guest_event_id}
                                className={`inline-flex items-center justify-center px-6 py-2.5 rounded-xl text-sm font-bold bg-[#33FF00] text-black shadow-lg shadow-[#33FF00]/20 hover:shadow-[#33FF00]/40 transform transition-all hover:-translate-y-0.5 active:translate-y-0 ${
                                    respondingTo === invitation.guest_event_id ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Join Event
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Companion Button - Visible for Accepted AND Rejected if companions exist */}
                            {(invitation.status === 'accepted' || invitation.status === 'rejected') && 
                             invitation.companions && invitation.companions.length > 0 && !isEventPast(invitation) && (
                              <button 
                                onClick={() => handleManageCompanions(invitation)}
                                className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                                  theme === 'dark'
                                    ? 'bg-purple-500/10 border-purple-500/20 text-purple-300 hover:bg-purple-500/20'
                                    : 'bg-purple-50 border-purple-100 text-purple-700 hover:bg-purple-100'
                                }`}
                              >
                                <Users className="h-4 w-4 mr-2" />
                                Companions
                              </button>
                            )}

                             {/* Details Button - Blue Theme */}
                             <button 
                              onClick={() => handleViewDetails(invitation)}
                              className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                                theme === 'dark'
                                  ? 'border-blue-800 bg-blue-900/20 text-blue-300 hover:bg-blue-900/40'
                                  : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                            >
                              Details
                            </button>

                            {/* Change Response Buttons (Text instead of Icons) - Colored by Action */}
                            {!isEventPast(invitation) && (
                               <button
                                  onClick={() => handleResponseClick(invitation, invitation.status === 'accepted' ? 'rejected' : 'accepted')}
                                  disabled={respondingTo === invitation.guest_event_id}
                                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                                    invitation.status === 'accepted'
                                      ? (theme === 'dark'
                                          ? 'border-orange-800 bg-orange-900/20 text-orange-300 hover:bg-orange-900/40'
                                          : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100')
                                      : (theme === 'dark'
                                          ? 'border-green-800 bg-green-900/20 text-green-300 hover:bg-green-900/40'
                                          : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100')
                                  }`} 
                                >
                                  {invitation.status === 'accepted' ? 'Change to Not Attending' : 'Change to Attending'}
                               </button>
                            )}
                        </>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Calendar className={`h-16 w-16 mx-auto mb-4 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <h3 className={`text-lg font-medium mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>No Invitations</h3>
              <p className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>You don't have any event invitations yet.</p>
            </div>
          )}
        </div>
        </CustomScrollbar>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedInvitation && selectedAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-3 sm:p-4">
          <div className={`relative w-full max-w-md rounded-xl sm:rounded-2xl shadow-2xl border backdrop-blur-sm max-h-[90vh] flex flex-col ${
            theme === 'dark' 
              ? 'bg-black/90 border-gray-800/50' 
              : 'bg-white/95 border-gray-200/50'
          }`}>
            {/* Modal Header - compact */}
            <div className={`px-4 sm:px-6 py-3 border-b flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-2">
                {selectedAction === 'accepted' ? (
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0" />
                )}
                <h3 className={`text-base sm:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {selectedAction === 'accepted' ? 'Accept Invitation' : 'Decline Invitation'}
                </h3>
              </div>
            </div>

            {/* Modal Content - scrollable, compact on mobile */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 overflow-y-auto flex-1 min-h-0">
              {/* Event image when available */}
              {selectedInvitation.image_url && (
                <div className="flex justify-center mb-3 sm:mb-4">
                  <div
                    className={`relative w-full max-w-[180px] sm:max-w-[220px] aspect-square overflow-hidden rounded-xl sm:rounded-2xl border ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    } ${getImageShapeStyles(selectedInvitation.image_shape).className || 'rounded-xl'}`}
                    style={getImageShapeStyles(selectedInvitation.image_shape).style}
                  >
                    <img
                      src={selectedInvitation.image_url}
                      alt={selectedInvitation.title}
                      className="w-full h-full object-cover"
                      style={getImageShapeStyles(selectedInvitation.image_shape).style}
                    />
                  </div>
                </div>
              )}
              {/* Confirmation question so intent is clear */}
              <p className={`text-center text-sm sm:text-base font-medium leading-snug mb-3 sm:mb-4 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                {selectedInvitation.status === 'pending'
                  ? (selectedAction === 'accepted' 
                      ? 'Are you sure you want to accept this invitation?'
                      : 'Are you sure you want to decline this invitation?')
                  : (selectedAction === 'accepted'
                      ? 'Are you sure you want to change your response to Accepted?'
                      : 'Are you sure you want to change your response to Declined?')
                }
              </p>

              {/* Event summary - compact card */}
              <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                theme === 'dark' ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {selectedInvitation.title}
                </h4>
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <span className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                    {selectedInvitation.host}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                    {new Date(selectedInvitation.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    {formatTime(selectedInvitation.time)}
                  </span>
                </div>
                {selectedInvitation.location && (
                  <div className={`flex items-center gap-1.5 mt-1.5 text-xs sm:text-sm truncate ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{selectedInvitation.location}</span>
                  </div>
                )}
                {selectedInvitation.category && (
                  <span className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    theme === 'dark'
                      ? 'bg-purple-900/30 text-purple-300'
                      : 'bg-purple-50 text-purple-700'
                  }`}>
                    {selectedInvitation.category}
                  </span>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-4 sm:px-6 py-3 border-t flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedInvitation(null);
                    setSelectedAction(null);
                    setCompanionResponses([]);
                  }}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200 hover:border-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmedResponse}
                  disabled={respondingTo === selectedInvitation.guest_event_id}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 hover:scale-105 ${
                    selectedAction === 'accepted'
                      ? (theme === 'dark'
                          ? 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
                          : 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25')
                      : (theme === 'dark'
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-600/25'
                          : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-600/25')
                  } ${respondingTo === selectedInvitation.guest_event_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {respondingTo === selectedInvitation.guest_event_id ? 'Processing...' : 
                   selectedAction === 'accepted' ? 'Accept Invitation' : 'Decline Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEventDetails && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEventDetailsModal(false);
              setSelectedEventDetails(null);
            }
          }}
        >
          <div className={`relative w-full max-w-2xl rounded-2xl shadow-2xl border backdrop-blur-sm max-h-[90vh] flex flex-col ${
            theme === 'dark' 
              ? 'bg-black/90 border-gray-800/50' 
              : 'bg-white/95 border-gray-200/50'
          }`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className={`text-xl font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Event Details
              </h3>
                </div>
                <button
                  onClick={() => {
                    setShowEventDetailsModal(false);
                    setSelectedEventDetails(null);
                  }}
                  className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6 overflow-y-auto overscroll-contain flex-1 min-h-0">
              {/* Event image when available */}
              {selectedEventDetails.image_url && (
                <div className="flex justify-center mb-6">
                  <div
                    className={`relative w-full max-w-[240px] sm:max-w-[280px] aspect-square overflow-hidden rounded-xl sm:rounded-2xl border shadow-lg ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    } ${getImageShapeStyles(selectedEventDetails.image_shape).className || 'rounded-xl'}`}
                    style={getImageShapeStyles(selectedEventDetails.image_shape).style}
                  >
                    <img
                      src={selectedEventDetails.image_url}
                      alt={selectedEventDetails.title}
                      className="w-full h-full object-cover"
                      style={getImageShapeStyles(selectedEventDetails.image_shape).style}
                    />
                  </div>
                </div>
              )}
              {/* Event Title and Category */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h4 className={`text-2xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {selectedEventDetails.title}
                  </h4>
                  {selectedEventDetails.category && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      theme === 'dark' 
                        ? 'bg-purple-900/30 text-purple-300 border border-purple-800/50' 
                        : 'bg-purple-50 text-purple-700 border border-purple-200'
                    }`}>
                      {selectedEventDetails.category}
                    </span>
                  )}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <User className="h-4 w-4 mr-1" />
                  Hosted by {selectedEventDetails.host}
                </div>
              </div>

              {/* Event Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Date & Time */}
                <div className={`p-4 rounded-xl border ${
                  theme === 'dark' 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200/50'
                }`}>
                  <div className="flex items-center mb-3">
                    <Clock className="h-5 w-5 text-blue-600 mr-2" />
                    <h5 className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Date & Time
                    </h5>
                  </div>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {(() => {
                      try {
                        return new Date(selectedEventDetails.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      } catch (error) {
                        return selectedEventDetails.date || 'Date not available';
                      }
                    })()}
                  </p>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {(() => {
                      try {
                        // Handle time string format like "16:33:00"
                        if (typeof selectedEventDetails.time === 'string' && selectedEventDetails.time.includes(':')) {
                          const [hours, minutes] = selectedEventDetails.time.split(':');
                          const timeDate = new Date();
                          timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                          
                          return timeDate.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });
                        }
                        
                        // Try parsing as a full date
                        const timeDate = new Date(selectedEventDetails.time);
                        if (!isNaN(timeDate.getTime())) {
                          return timeDate.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });
                        }
                        
                        return selectedEventDetails.time || 'Time not available';
                      } catch (error) {
                        return selectedEventDetails.time || 'Time not available';
                      }
                    })()}
                  </p>
                </div>

                {/* Location */}
                <div className={`p-4 rounded-xl border ${
                  theme === 'dark' 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200/50'
                }`}>
                  <div className="flex items-center mb-3">
                    <MapPin className="h-5 w-5 text-green-600 mr-2" />
                    <h5 className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Location
                    </h5>
                  </div>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {selectedEventDetails.location}
                    {selectedEventDetails.location_link && (
                      <>
                        {' · '}
                        <a
                          href={selectedEventDetails.location_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center ${
                            theme === 'dark'
                              ? 'text-[#33FF00] hover:text-[#2BD400]'
                              : 'text-blue-600 hover:text-blue-700'
                          } underline`}
                        >
                          <LinkIcon className="h-3.5 w-3.5 mr-1" />
                          Open in Maps
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedEventDetails.description && (
                <div className={`p-4 rounded-xl border mb-6 ${
                  theme === 'dark' 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200/50'
                }`}>
                  <div className="flex items-center mb-3">
                    <MessageSquare className="h-5 w-5 text-purple-600 mr-2" />
                    <h5 className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Description
                    </h5>
                  </div>
                  <p className={`text-sm leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {selectedEventDetails.description}
                  </p>
                </div>
              )}

              {/* Companion Information */}
              {(selectedEventDetails.companion_count > 0 || (selectedEventDetails.companions && selectedEventDetails.companions.length > 0)) && (
                <div className={`p-4 rounded-xl border mb-6 ${
                  theme === 'dark' 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200/50'
                }`}>
                  <div className="flex items-center mb-3">
                    <Users className="h-5 w-5 text-orange-600 mr-2" />
                    <h5 className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Companion Information
                    </h5>
                  </div>
                  
                  {/* Detailed Companion Status List */}
                  {selectedEventDetails.companions && selectedEventDetails.companions.length > 0 ? (
                    <div className="space-y-3 mt-3">
                        <div className="flex justify-between items-center mb-2">
                             <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                Companions ({selectedEventDetails.companions.length})
                            </span>
                        </div>
                        {selectedEventDetails.companions.map((companion: any) => (
                        <div key={companion.guest_event_id} className={`flex items-center justify-between p-2.5 rounded-lg border ${
                            theme === 'dark' ? 'bg-black/20 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                            <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                            {companion.name}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            companion.status === 'accepted'
                                ? (theme === 'dark' ? 'bg-[#33FF00]/10 text-[#33FF00] border border-[#33FF00]/20' : 'bg-green-50 text-green-700 border border-green-200')
                                : companion.status === 'rejected'
                                ? (theme === 'dark' ? 'bg-red-900/20 text-red-400 border border-red-800/30' : 'bg-red-50 text-red-700 border border-red-200')
                                : (theme === 'dark' ? 'bg-orange-900/20 text-orange-400 border border-orange-800/30' : 'bg-orange-50 text-orange-700 border border-orange-200')
                            }`}>
                            {companion.status.charAt(0).toUpperCase() + companion.status.slice(1)}
                            </span>
                        </div>
                        ))}
                    </div>
                  ) : (
                    /* Fallback for legacy text-only display */
                    <div className="space-y-2">
                        <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                        <span className="font-medium">Allowed companions:</span> {selectedEventDetails.companion_count}
                        </p>
                        {selectedEventDetails.companion_name && (
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                            <span className="font-medium">Companion names:</span> {selectedEventDetails.companion_name}
                        </p>
                        )}
                    </div>
                  )}
                </div>
              )}

              {/* Status Badge */}
              <div className="flex items-center justify-center">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(selectedEventDetails.status, isEventPast(selectedEventDetails))}`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    isEventPast(selectedEventDetails) ? 'bg-gray-400' :
                    selectedEventDetails.status === 'accepted' ? 'bg-[#33FF00]' :
                    selectedEventDetails.status === 'rejected' ? 'bg-red-500' :
                    selectedEventDetails.status === 'pending' ? 'bg-orange-500' :
                    'bg-gray-500'
                  }`}></span>
                  {isEventPast(selectedEventDetails) ? 'Ended Event' : selectedEventDetails.status === 'accepted' ? 'Attending' : `${selectedEventDetails.status.charAt(0).toUpperCase() + selectedEventDetails.status.slice(1)} Event`}
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowEventDetailsModal(false);
                    setSelectedEventDetails(null);
                  }}
                  className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200 hover:border-gray-400'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Companion Management Modal */}
      {showCompanionModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-black/90 border-gray-800/50' 
              : 'bg-white/95 border-gray-200/50'
          }`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center">
                <Users className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Manage Companions
                </h3>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              <p className={`text-sm mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Please confirm or decline attendance for each of your companions.
              </p>

              <div className="space-y-0 divide-y border rounded-xl overflow-hidden">
                {companionResponses.map((companion, index) => (
                  <div key={companion.guest_event_id} className={`p-4 flex items-center justify-between ${
                        theme === 'dark' ? 'bg-gray-800/30 border-gray-700 divide-gray-700' : 'bg-gray-50 border-gray-200 divide-gray-200'
                    }`}>
                    <div className="flex items-center">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 font-medium text-xs ${
                            theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-white border text-gray-600'
                        }`}>
                            {companion.name.charAt(0).toUpperCase()}
                        </div>
                        <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {companion.name}
                        </span>
                    </div>
                    
                    <div className={`flex rounded-full p-1 ${
                        theme === 'dark' ? 'bg-gray-900' : 'bg-white border'
                    }`}>
                        <button
                            onClick={() => handleCompanionStatusChange(index, 'rejected')}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                companion.status === 'rejected'
                                    ? 'bg-red-500 text-white shadow-sm'
                                    : (theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                            }`}
                        >
                            Decline
                        </button>
                        <button
                            onClick={() => handleCompanionStatusChange(index, 'accepted')}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                companion.status === 'accepted'
                                    ? 'bg-[#33FF00] text-black shadow-sm'
                                    : (theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                            }`}
                        >
                            Confirm
                        </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCompanionModal(false);
                    setSelectedInvitation(null);
                    setCompanionResponses([]);
                  }}
                  className={`flex-1 px-4 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200 hover:border-gray-400'
                  }`}
                  disabled={respondingTo === selectedInvitation.guest_event_id}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCompanions}
                  disabled={respondingTo === selectedInvitation.guest_event_id}
                  className={`flex-1 px-4 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
                      : 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
                  } ${respondingTo === selectedInvitation.guest_event_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {respondingTo === selectedInvitation.guest_event_id ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GuestDashboard;
